import {injectable} from "inversify";
import {ShopService} from "@/services/shop-service";
import {TabbedPanel} from "@/scenes.ui/tabbed-panel";
import {Tr} from "@/i18n/i18n";
import ShopStore from "@/store/shop";
import {Player} from "@/store/player/types";
import PlayerStore from "@/store/player";
import {OblivionHeroDto, OblivionTokenDto} from "@/store/shop/types";
import Container = Phaser.GameObjects.Container;
import SoundUtils from "@/utils/sound-utils";
import {FeedbackPanel} from "@/scenes.ui/feedback-panel";
import {CreatureUtils} from "@/utils/creature-utils";
import {NewCreaturePanel} from "@/scenes.ui/new-creature-panel";
import {BuyCreatureConfirmationPanel} from "@/scenes.ui/buy-creature-confirmation-panel";
import {OblivioinHeroPanel} from "@/scenes.ui/oblivion-hero-panel";
import {OblivionHeroInfoPanel} from "@/scenes.ui/oblivion-hero-info-panel";
import {HeroTokenPanel} from "@/scenes.ui/hero-token-panel";
import CreatureStore from "@/store/creature";
import {ConfirmationPanel} from "@/scenes.ui/confirmation-panel";
import ClanStore from "@/store/clan";
import {SceneName} from "@/model/scene-name";
import {Keys} from "@/constants/key";
import {TwoOptionsPanel} from "@/scenes.ui/two-options-panel";

@injectable()
export class OblivionShopService extends ShopService {

    private tokens: number = 0;

    protected createTabbedPanel(): TabbedPanel {
        let self = this;
        return new TabbedPanel(this.scene!, this.canvas!, Tr('ark'), ['bank', 'heroes'],
            [Tr('tokens'), Tr('geroi')], [true, true], this.selectSound, (tabId: string) => {
                if (tabId === 'bank') {
                    this.populateTokenTab();
                } else if (tabId === 'heroes') {
                    this.populateHeroTab();
                }
            }, () => {}, () => self.closeCallback());
    }

    protected populateTokenTab(): void {
        let self = this;

        let player = PlayerStore.getPlayer()!;

        const panels: Container[] = [];
        let panelY = 0;
        ShopStore.fetchOblivionToken().then((ot: OblivionTokenDto) => {
            self.tokens = ot.tokens;
            self.setupHeroAgitations(player);
            panelY = self.showBankOptions(ot.bankOptions, [], panels, player);

            for (let ct of ot.creatureToTokens) {
                let c = CreatureUtils.getCreatureById(ct.creatureId);
                panels.push(new HeroTokenPanel(self.scene!, 0, panelY, ct, c, () => {

                    if (c.identifier === 'sur_ns') {
                        let panel: TwoOptionsPanel = new TwoOptionsPanel(self.scene!, self.canvas!, 'ОБМЕН', 'СОВЕРШИТЬ ОБМЕН?',
                            undefined, Keys.TOKEN_BIG, 'При обмене Нук-Серра пропадет из Вашей команды, а Вы получите 50 токенов.',
                            '* На портал Призыва обмен не повлияет: в Портале призыва даже обмененная Нук-Серра учитывается при подсчете количества.',
                            () => {
                                SoundUtils.play(self.awardSound);
                                ShopStore.sellInArk(ct.creatureId).then((player: Player) => {
                                    self.tabbedPanel.closePanel();
                                });
                            }, Tr('do.exch'));
                        panel.openAnimated();
                    } else {
                        let cp = new ConfirmationPanel(self.scene!, self.canvas!, () => {
                            SoundUtils.play(self.awardSound);
                            ShopStore.sellInArk(ct.creatureId).then((player: Player) => {
                                self.tabbedPanel.closePanel();
                            });
                        }, Tr('podtverzhdenie'), 'Продать героя за токены?', Keys.TOKEN_BIG, Tr('da'), Tr('net'));
                        cp.openAnimated();
                    }
                }));
            }

            this.tabbedPanel.supplyChildren('bank', panels);
        });
    }

    protected populateHeroTab(): void {
        let self = this;

        let player = PlayerStore.getPlayer()!;

        ShopStore.fetchOblivionHero().then((oh: OblivionHeroDto) => {
            self.tokens = oh.tokens;
            self.setupHeroAgitations(player);
            let panels: Container[] = [];

            let panelY = 0;
            oh.creatures.forEach(sc => {
                let classText = CreatureUtils.getClassName(sc.creature.warriorClass);
                if (!classText) {
                    return;
                }

                let scp = new OblivioinHeroPanel(this.scene!, 0, panelY, sc, self.statClickCallback, () => {
                    if (self.tokens < sc.price) {
                        SoundUtils.play(self.levelUpSound);
                        this.tabbedPanel.goToTab('bank');
                        new FeedbackPanel(self.scene!, Tr('no.tok'), self.tabbedPanel!, false).show();
                    } else {
                        let isSquadFull = CreatureUtils.findNonTemporalCreatures(PlayerStore.getPlayer().creatures).length >= ShopService.MAX_CREATURES;
                        let confirmPanel: BuyCreatureConfirmationPanel = new BuyCreatureConfirmationPanel(this.scene!, this.canvas!,
                            this.tabbedPanel!.depth + 1, sc, () => {
                                SoundUtils.play(self.awardSound);
                                ShopStore.buyCreatureToken(sc.id).then(() => {
                                    sc.creatures.forEach(c => {
                                        new NewCreaturePanel(self.scene!, self.canvas!, c).open();
                                    });
                                });
                            }, isSquadFull, ShopService.MAX_CREATURES);
                        confirmPanel.open();
                    }
                });

                panels.push(scp);
                panelY += OblivioinHeroPanel.OBLIVION_HERO_PANEL_HEIGHT;
            });

            panelY = self.showBankOptions(oh.bankOptions, [], panels, player);

            panels.push(new OblivionHeroInfoPanel(self.scene!, self.canvas!));

            this.tabbedPanel.supplyChildren('heroes', panels);
        });
    }

    protected setupHeroAgitations(player: Player) {
        this.tabbedPanel.setAgitationText(Tr('ur.tokens') + ' ' + this.tokens);
    }
}
